import React, { Fragment, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import SubsetOptionsSelect from "../../components/RealtimeProductConfigurator/SubsetOptionsSelect";
import Button from "react-bootstrap/Button";
import ConfigurationPreview from "../../components/RealtimeProductConfigurator/ConfigurationPreview";
import { IProductFormValues } from "../../components/RealtimeProductConfigurator/ProductForm";
import { useParams } from "react-router-dom";
import { OrderCategory } from "../../api/endpoints/Order";
import * as Api from "../../api";
import PageTitle from "../../components/Ui/PageTitle";
import { TApiSubset } from "../../api/endpoints/Subset";
import { TApiSubsetOptions } from "../../api/endpoints/SubsetOption";
import { productToValues, valuesToProduct } from "../../utils/ProductFormHelpers";
import { paths } from "../../router";
import { useHistory } from "react-router";
import { FormikHelpers } from "formik";
import LoadingIndicator from "../../components/Feedback/LoadingIndicator";
import { Nullable } from "../../../types/utils";
import { TOrderProduct, TOrderProductData } from "../../api/endpoints/OrderProduct";

interface IProps {
}

interface IRouteParams {
  orderId: string;
  productId: string;
}

const RealtimeOrderProduct = (props: IProps) => {
  const history = useHistory();
  const params = useParams<IRouteParams>();
  const [productFormValues, setProductFormValues] = useState<IProductFormValues | null>(null);
  const [initialProductFormValues, setInitialProductFormValues] = useState<IProductFormValues | null>(null);

  const [product, setProduct] = useState<TOrderProduct<OrderCategory.REALTIME> | null>(null);
  const [productSubset, setProductSubset] = useState<TApiSubset | null>(null);
  const [error, setError] = useState<Nullable<string>>(null);

  useEffect(() => {
    Api.OrderProduct.fetch(params.orderId, params.productId).then(product => {
      setProduct(product)
    }).catch(() => {
      setError('Page not found.');
    });
  }, [params.orderId, params.productId]);

  useEffect(() => {
    if (product) {
      setInitialProductFormValues(productToValues(product.data));

      const subsetId = product.data.subset_id;

      Api.Subset.fetch(subsetId).then(setProductSubset);
    }
  }, [product]);

  const onSubmit = async (
    subset: TApiSubset,
    subsetOptions: TApiSubsetOptions,
    values: IProductFormValues,
    formikHelpers: FormikHelpers<IProductFormValues>
  ) => {
    if (!product) {
      return;
    }

    const updatedData = valuesToProduct(subset, subsetOptions, values);

    try {
      const updatedProduct = await Api.OrderProduct.update(product.order_id, product.id, updatedData);

      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values });

      history.push(paths.order(updatedProduct.order_id));
    } catch (error) {
      formikHelpers.setSubmitting(false);
    }
  };

  const onProductValuesChange = (values: IProductFormValues) => {
    setProductFormValues(values)
  };

  if (!product || !productSubset) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <div>Page not found.</div>
  }

  return (
    <Fragment>
      <Row>
        <Col>
          <PageTitle title="Edit product" />
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={12} md={8}>
          <SubsetOptionsSelect
            subsetId={productSubset.id}
            initialValues={initialProductFormValues || undefined}
            actions={
              (subset, subsetOptions, formik) => (
                <Button
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Updating...' : 'Update'}
                </Button>
              )
            }
            onSubmit={onSubmit}
            onChange={onProductValuesChange}
          />
        </Col>

        <Col md={4}>
          <Card className="product-configurator-preview">
            <Card.Body>
              {
                <ConfigurationPreview
                  subset={productSubset}
                  values={productFormValues}
                />
              }
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  )
}

export default RealtimeOrderProduct;
